import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useMemo } from "react";
import { USER_API, toastMsg } from "config";
import {
  BudgetFringe,
  BudgetFringeCeilingType,
  BudgetFringeUnit,
  BudgetItem,
} from "components/dashboard/body/projects/panels/budget/types";
import { budgetKeys } from "queries";
import { getToastMessageForError, handleToast, normalizeId, ServerError } from "utils";
import { useAxios } from "hooks/useAxios";
import { type ScopedRequest, useScopedMutation } from "hooks/useScopedMutation";
import {
  BudgetSummaryCache,
  patchBudgetItemInCache,
  unwrapBudgetApiData,
} from "./budgetCachePatch";

interface BudgetFringeScope {
  projectId: string | number;
  documentId: string | number;
  budgetId: string | undefined;
}

export interface UpdateBudgetFringePatch {
  name?: string;
  rate?: number;
  unit?: BudgetFringeUnit;
  /** `null` removes the ceiling. */
  ceiling?: number | null;
  ceiling_type?: BudgetFringeCeilingType;
  account_number?: string;
  /** Every line the fringe applies to. `[]` keeps the fringe with no lines. */
  item_ids?: number[];
}

export interface UpdateBudgetItemFringesParams {
  itemId: number;
  /** The line's full fringe list after the change. */
  fringes: BudgetFringe[];
}

const toastFringeError = (error: unknown) => {
  const serverError = isAxiosError(error) ? ServerError.fromAxiosError(error) : null;
  handleToast(
    serverError ? getToastMessageForError(serverError) : toastMsg.genericServerErr,
    "error"
  );
};

/** Every fringe in the budget, including ones that apply to no lines. */
export function useBudgetFringes({ projectId, documentId, budgetId }: BudgetFringeScope) {
  const axios = useAxios({ allResponse: false });
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  return useQuery({
    queryKey: budgetKeys.fringes(pid, did, budgetId ?? ""),
    queryFn: async (): Promise<BudgetFringe[]> => {
      const response = await axios.get(USER_API.getBudgetFringes(pid, did, budgetId ?? ""));
      const fringes = unwrapBudgetApiData<BudgetFringe[]>(response);
      return Array.isArray(fringes) ? fringes : [];
    },
    enabled: Boolean(budgetId) && Boolean(pid) && Boolean(did),
  });
}

/**
 * The draft and budget a fringe request was made in. The budget page stays mounted across a
 * draft switch, and React Query hands a request each new render's callbacks until it settles
 * (also while it waits offline), so every fringe request carries this (see useScopedMutation).
 */
export interface FringeRequestScope {
  pid: string;
  did: string;
  budgetId: string | undefined;
}

export function useFringeRequestScope({
  projectId,
  documentId,
  budgetId,
}: BudgetFringeScope): FringeRequestScope {
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);
  return useMemo(() => ({ pid, did, budgetId }), [pid, did, budgetId]);
}

/** A request's budget id, or the error every fringe write has always thrown without one. */
export function fringeBudgetIdOf(target: FringeRequestScope): string {
  if (!target.budgetId) throw new Error("Budget ID is not available");
  return target.budgetId;
}

const summaryKeyOf = (target: FringeRequestScope) => budgetKeys.summary(target.pid, target.did);

export function useUpdateBudgetFringe(fringeScope: BudgetFringeScope) {
  const axios = useAxios({ allResponse: false });
  const queryClient = useQueryClient();
  const scope = useFringeRequestScope(fringeScope);

  return useScopedMutation(scope, {
    mutationFn: async ({
      variables: { fringeId, patch },
      scope: target,
    }: ScopedRequest<
      { fringeId: number; patch: UpdateBudgetFringePatch },
      FringeRequestScope
    >): Promise<BudgetFringe> => {
      const response = await axios.patch(
        USER_API.updateBudgetFringe(
          target.pid,
          target.did,
          fringeBudgetIdOf(target),
          String(fringeId)
        ),
        patch
      );
      return unwrapBudgetApiData<BudgetFringe>(response);
    },
    onSuccess: (_data, { scope: target }) =>
      queryClient.invalidateQueries({ queryKey: summaryKeyOf(target) }),
    onError: toastFringeError,
  });
}

export function useDeleteBudgetFringe(fringeScope: BudgetFringeScope) {
  const axios = useAxios({ allResponse: false });
  const queryClient = useQueryClient();
  const scope = useFringeRequestScope(fringeScope);

  return useScopedMutation(scope, {
    mutationFn: async ({
      variables: fringeId,
      scope: target,
    }: ScopedRequest<number, FringeRequestScope>): Promise<void> => {
      await axios.delete(
        USER_API.deleteBudgetFringe(
          target.pid,
          target.did,
          fringeBudgetIdOf(target),
          String(fringeId)
        )
      );
    },
    onSuccess: (_data, { scope: target }) =>
      queryClient.invalidateQueries({ queryKey: summaryKeyOf(target) }),
    onError: toastFringeError,
  });
}

/** Sets one line's fringes. Totals update at once from the full fringe objects. */
export function useUpdateBudgetItemFringes(fringeScope: BudgetFringeScope) {
  const axios = useAxios({ allResponse: false });
  const queryClient = useQueryClient();
  const scope = useFringeRequestScope(fringeScope);

  return useScopedMutation(scope, {
    mutationFn: async ({
      variables: { itemId, fringes },
      scope: target,
    }: ScopedRequest<UpdateBudgetItemFringesParams, FringeRequestScope>): Promise<BudgetItem> => {
      const response = await axios.patch(
        USER_API.updateBudgetInfo(target.pid, target.did, fringeBudgetIdOf(target), String(itemId)),
        { fringes: fringes.map((fringe) => fringe.budget_fringe_id) }
      );
      return unwrapBudgetApiData<BudgetItem>(response);
    },
    onMutate: async ({ variables: { itemId, fringes }, scope: target }) => {
      await queryClient.cancelQueries({ queryKey: summaryKeyOf(target), exact: true });
      queryClient.setQueryData<BudgetSummaryCache>(summaryKeyOf(target), (old) =>
        patchBudgetItemInCache(old, itemId, { fringes })
      );
    },
    // The optimistic patch already matches what the server saved, so success needs no refetch.
    // A failure reloads the budget the change started in. Putting back a snapshot from before
    // the change could erase saves the server confirmed meanwhile.
    onError: (error, { scope: target }) => {
      queryClient.invalidateQueries({ queryKey: summaryKeyOf(target) });
      toastFringeError(error);
    },
  });
}
